import { ActionIcon, ColorInput, Group, Stack, Text } from '@mantine/core';
import { IconPlus, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { componentsTestIds } from '@components/testids';
import { isCustomTint } from '../../../../WidgetCard/tintTokens';
import { widgetDataSourceOf } from '../../../../widgetCatalog';
import type { WidgetType } from '../../../types';
import type { WidgetDefinition } from '../../types';

/**
 * Author-chosen colours for a chart's SERIES (2026-08-17).
 *
 * The card tint answers "what colour is this card"; this answers "what colour is
 * the DATA" — the bars, the lines, the slices — which is the request behind "I
 * want to be able to choose colours for everything when editing widgets".
 *
 * ## Why a POSITIONAL, PARTIAL list
 *
 * A chart's series count comes from the DATA and moves with it: add a sales
 * channel and yesterday's two-series chart has three. So this is deliberately NOT
 * "one colour per series" — it is an override LIST, where entry `i` colours series
 * `i` and anything past the end rides the built-in palette (`seriesColorAt` in
 * `WebWidget.tsx`). Picking the first colour and leaving the rest is the common
 * case, and it cannot be invalidated by tomorrow's data.
 *
 * That also means there is no name to show beside each swatch: at configuration
 * time the series are not known — they are discovered from the result set. Showing
 * "Series 1 / 2 / 3" is honest about that; inventing labels would not be.
 *
 * ## Storage
 *
 * `widgetComponentProps.seriesColors`, so NO schema change: every widget's
 * `widgetComponentProps` is `additionalProperties: true` in the domain schema. The
 * values are validated with the same `isCustomTint` guard the card tint uses —
 * `#rrggbb` and nothing else, because this reaches the DOM on a SHARED document.
 */
type Props = {
  widgetType: WidgetType;
  config: WidgetDefinition;
  updateConfig: ( key: keyof WidgetDefinition, value: unknown ) => void;
};

/**
 * Only types that actually PLOT a series take this.
 *
 * Derived from the catalog's data source plus an explicit chart check rather than
 * a hard-coded list: a `kpi` is `analytics` too but draws one number, and a
 * `table` draws rows. Both would show a control that changes nothing.
 */
const SERIES_CHART_TYPES: ReadonlySet<WidgetType> = new Set<WidgetType>([
  'areaChart', 'barChart', 'lineChart', 'pieChart', 'donutChart', 'scatterChart'
]);

export const hasSeriesColors = ( type: WidgetType ): boolean =>
  SERIES_CHART_TYPES.has( type ) && widgetDataSourceOf( type ) === 'analytics';

export default function SeriesColorPicker({ widgetType, config, updateConfig }: Props ) {
  const { t } = useTranslation( 'widgets' );

  if ( !hasSeriesColors( widgetType )) return null;

  const props = ( config.widgetComponentProps ?? {}) as Record<string, unknown>;
  const colors: string[] = Array.isArray( props.seriesColors )
    ? ( props.seriesColors as string[])
    : [];

  /*
   * One write of the whole props bag per change. `updateConfig` replaces the key
   * it is given, so reading-modifying-writing `widgetComponentProps` wholesale is
   * what keeps a colour edit from dropping every other prop on the widget.
   */
  const commit = ( next: string[]) =>
    updateConfig( 'widgetComponentProps', { ...props, seriesColors: next });

  const setAt = ( index: number, value: string ) => {
    // Emitted per keystroke while a hex is typed, so a partial value must not be
    // stored — it would fail the render-time guard and silently fall back to the
    // default, which reads as the picker not working.
    if ( !isCustomTint( value )) return;
    const next = [...colors];
    next[index] = value;
    commit( next );
  };

  const removeAt = ( index: number ) => commit( colors.filter(( _, i ) => i !== index ));

  return (
    <Stack gap="xxs" data-testid={componentsTestIds.reportsInspectorSeriesColors}>
      <Text className={undefined} size="xs" fw={600}>
        {t( 'widget_series_colors_label', 'Colores de las series' )}
      </Text>

      {colors.map(( color, index ) => (
        <Group key={index} gap="xxs" wrap="nowrap">
          {/*
            `hexa`, not `hex` (2026-08-18) — a series colour is an element colour
            like any other, and opacity is the author's call. The eight-digit
            spelling is what `isCustomTint` accepts, so it round-trips; a chart
            series at partial alpha is also how overlapping areas stay readable.
          */}
          <ColorInput
            size="xs"
            format="hexa"
            withEyeDropper
            style={{ flex: 1 }}
            data-testid={componentsTestIds.reportsInspectorSeriesColor( index )}
            aria-label={t( 'widget_series_colors_item', 'Serie {{n}}', { n: index + 1 })}
            placeholder={t( 'widget_series_colors_item', 'Serie {{n}}', { n: index + 1 })}
            value={isCustomTint( color ) ? color : ''}
            onChange={( value ) => setAt( index, value )}
          />
          <ActionIcon
            size="sm"
            variant="subtle"
            color="gray"
            data-testid={componentsTestIds.reportsInspectorSeriesColorRemove( index )}
            aria-label={t( 'widget_series_colors_remove', 'Quitar color' )}
            onClick={() => removeAt( index )}
          >
            <IconX size={14} />
          </ActionIcon>
        </Group>
      ))}

      <ActionIcon
        size="sm"
        variant="subtle"
        data-testid={componentsTestIds.reportsInspectorSeriesColorAdd}
        aria-label={t( 'widget_series_colors_add', 'Agregar color' )}
        // Seeded from the palette's own next entry rather than an arbitrary value,
        // so adding a slot changes nothing until the author actually picks — the
        // control starts by AGREEING with what is already on screen.
        onClick={() => commit([ ...colors, '#4dabf7' ])}
      >
        <IconPlus size={14} />
      </ActionIcon>

      <Text size="xs" c="dimmed">
        {t(
          'widget_series_colors_hint',
          'Cada color pinta una serie, en orden. Las que no tengan color usan la paleta por defecto.'
        )}
      </Text>
    </Stack>
  );
}
